import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import FlowDocuments from "./applicationFlow/FlowDocuments";
import { reactivateApplication, withdrawApplication } from "./admissionApi";
import { formatDisplayDateTime } from "utils/dateFormat";
import WithdrawalSettlementPanel from "./WithdrawalSettlementPanel";
import { fetchWithdrawalSettlement } from "services/withdrawalSettlementApi";
import { canAdjustAgencyFinancials, hasPermissionKey } from "utils/permissionUtils";
import { P } from "const/permissions";

const EMPTY_SETTLEMENT = {
  withdrawalTermUuid: null,
  settlementMode: "COLLECT_FULL",
  settledAgreed: "",
  retention: [],
};

const ApplicationWithdrawPanel = ({
  admissionId,
  withdrawn = false,
  withdrawReason = "",
  withdrawnAt = null,
  withdrawnDocuments = [],
  statusBeforeWithdrawal = "",
  onWithdrawn,
  onReactivated,
  inline = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [documents, setDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [settlement, setSettlement] = useState(EMPTY_SETTLEMENT);
  const [settlementTerms, setSettlementTerms] = useState([]);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const queryClient = useQueryClient();
  // Withdrawing is an admissions job; what commission survives it is the accounts team's call, made
  // later from the account editor. Without that right the retention block never renders and the
  // payload never carries one.
  const canAdjustFinancials = canAdjustAgencyFinancials();
  // Withdrawing and reactivating both rewrite the application's status and its term schedule, so
  // they need the same right as editing it. Gated here rather than at each of the four call sites.
  const canWithdraw = hasPermissionKey(P.ADMISSIONS.UPDATE);

  // Both dialogs need the same term picture: the withdraw dialog to settle against it, the
  // reactivate dialog to warn when a refund has already gone out against a term it is about to
  // restore to its full fee.
  const panelOpen = dialogOpen || reactivateOpen;
  useEffect(() => {
    if (!panelOpen || !admissionId) return undefined;
    let cancelled = false;
    setLoadingTerms(true);
    fetchWithdrawalSettlement(admissionId)
      .then((data) => {
        if (cancelled) return;
        const row = data?.accountRows?.[0];
        const terms = row?.terms || [];
        setSettlementTerms(terms);
        const firstUnpaid = terms.find((t) => t.agreed - t.netPaid > 0);
        setSettlement((prev) => ({
          ...prev,
          withdrawalTermUuid: prev.withdrawalTermUuid ?? firstUnpaid?.termUuid ?? null,
        }));
      })
      .catch(() => {
        if (!cancelled) setSettlementTerms([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTerms(false);
      });
    return () => {
      cancelled = true;
    };
  }, [panelOpen, admissionId]);

  const hasSettlementStep = settlementTerms.length > 0;

  const handleWithdraw = async (settlementOverride) => {
    if (!reason.trim()) {
      toast.warning("Please enter a reason for withdrawal.");
      return;
    }
    if (!admissionId) return;
    const chosen = settlementOverride ?? settlement;
    const payload = chosen?.withdrawalTermUuid
      ? { ...chosen, retention: canAdjustFinancials ? chosen.retention : [] }
      : null;
    setSaving(true);
    try {
      await withdrawApplication(admissionId, {
        reason: reason.trim(),
        documents,
        settlement: payload,
      });
      toast.success("Application withdrawn.");
      setDialogOpen(false);
      setReason("");
      setDocuments([]);
      setStep(0);
      setSettlement(EMPTY_SETTLEMENT);
      await queryClient.invalidateQueries(["admission-clients-page"]);
      onWithdrawn?.({
        applicationWithdrawn: true,
        applicationWithdrawnReason: reason.trim(),
        applicationWithdrawnDocuments: documents,
        status: "Application Withdraw",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to withdraw application."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async () => {
    if (!admissionId) return;
    setSaving(true);
    try {
      await reactivateApplication(admissionId);
      toast.success("Application reactivated — you can continue from where you left off.");
      setReactivateOpen(false);
      await queryClient.invalidateQueries(["admission-clients-page"]);
      onReactivated?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to reactivate application."
      );
    } finally {
      setSaving(false);
    }
  };

  if (withdrawn) {
    return (
      <Alert severity="warning" className="border border-amber-300">
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Application withdrawn
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          All application form steps are locked. Reactivate to continue from
          {statusBeforeWithdrawal ? ` "${statusBeforeWithdrawal}"` : " the previous stage"}.
        </Typography>
        {withdrawReason ? (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>
            <strong>Reason:</strong> {withdrawReason}
          </Typography>
        ) : null}
        {withdrawnAt ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Withdrawn {formatDisplayDateTime(withdrawnAt)}
          </Typography>
        ) : null}
        {withdrawnDocuments?.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <FlowDocuments documents={withdrawnDocuments} onChange={() => {}} readOnly />
          </Box>
        )}
        {canWithdraw && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => setReactivateOpen(true)}
          >
            Reactivate application
          </Button>
        )}

        <Dialog
          open={reactivateOpen}
          onClose={() => !saving && setReactivateOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Reactivate this application?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              The application form will unlock and status will return to where it was before
              withdrawal. Every term returns to its original fee.
            </Typography>
            {settlementTerms.some((term) => term.netPaid < 0) && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                A refund has already been paid out against this admission. Reactivating restores
                every term&apos;s full fee, so settle that refund manually if the student is
                continuing.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReactivateOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleReactivate} disabled={saving}>
              {saving ? "Reactivating…" : "Reactivate"}
            </Button>
          </DialogActions>
        </Dialog>
      </Alert>
    );
  }

  if (!canWithdraw) {
    return null;
  }

  const withdrawButton = (
    <Button variant="outlined" color="warning" size="small" onClick={() => setDialogOpen(true)}>
      Withdraw application
    </Button>
  );

  const withdrawDialog = (
    <Dialog
      open={dialogOpen}
      onClose={() => !saving && setDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Withdraw application</DialogTitle>
      <DialogContent>
        {hasSettlementStep && (
          <Stepper activeStep={step} sx={{ mb: 2.5 }}>
            <Step>
              <StepLabel>Reason</StepLabel>
            </Step>
            <Step>
              <StepLabel>Money</StepLabel>
            </Step>
          </Stepper>
        )}

        {step === 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This locks the application form until you reactivate it. Your accounts team and super
              admins will be notified.
            </Typography>
            <TextField
              label="Reason for withdrawal"
              required
              fullWidth
              multiline
              minRows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Supporting documents (optional)
            </Typography>
            <FlowDocuments
              documents={documents}
              onChange={setDocuments}
              addLabel="Upload document"
            />
            {loadingTerms && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading this admission&apos;s terms…
                </Typography>
              </Box>
            )}
          </>
        )}

        {step === 1 && (
          <WithdrawalSettlementPanel
            terms={settlementTerms}
            value={settlement}
            onChange={setSettlement}
            canAdjustFinancials={canAdjustFinancials}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => (step === 1 ? setStep(0) : setDialogOpen(false))} disabled={saving}>
          {step === 1 ? "Back" : "Cancel"}
        </Button>
        {step === 0 && hasSettlementStep && (
          <Button
            variant="contained"
            onClick={() => setStep(1)}
            disabled={saving || !reason.trim()}
          >
            Next
          </Button>
        )}
        {step === 1 && (
          <Button
            onClick={() => handleWithdraw({ ...settlement, settlementMode: "PENDING" })}
            disabled={saving || !settlement.withdrawalTermUuid}
          >
            Decide later
          </Button>
        )}
        {(step === 1 || !hasSettlementStep) && (
          <Button
            color="warning"
            variant="contained"
            onClick={() => handleWithdraw()}
            disabled={saving}
          >
            {saving ? "Withdrawing…" : "Confirm withdrawal"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  if (inline) {
    return (
      <>
        {withdrawButton}
        {withdrawDialog}
      </>
    );
  }

  return (
    <Box className="flex justify-end">
      {withdrawButton}
      {withdrawDialog}
    </Box>
  );
};

export default ApplicationWithdrawPanel;
